import { publicEnv } from '@/utils/public-env'

/**
 * What environment is this process actually serving?
 *
 * `NODE_ENV` cannot answer that question. It has two values and three
 * meanings: a local `next start` is `production` but is not a deployment, and
 * stage is a deployment that is also `production`. Keying cookie behaviour or a
 * safety guard on it therefore fails in both directions, which is exactly what
 * happened twice:
 *
 * - **Auth cookies.** `isHttps` was `authUrl.startsWith('https://') || isProd`,
 *   so a local production build forced `secure: true`, `sameSite: 'None'` and a
 *   `.worldvue.com` cookie domain. None of those survive `http://localhost`, so
 *   SSO logged success server-side and the browser bounced straight back to
 *   sign-in — a local production build could not hold a session at all, and
 *   prod-only bugs could not be reproduced locally.
 * - **Email.** The recipient allowlist fell back to a safe list only when
 *   `NODE_ENV === 'development'`, so a local production build sent
 *   **unrestricted** — against a database restored from a prod-derived seed,
 *   where the user rows are real people.
 *
 * The two signals that do answer it, and why:
 *
 * - `APP_ENV` — set per environment by the deploy (`prod` / `stage` / `next`).
 *   The helpers below read it server-side only; the browser's copy comes from
 *   `/api/public-env` and is used just for tagging (`sentryEnvironment`).
 * - `NEXT_PUBLIC_AUTH_URL` — the literal address the app is served from, set
 *   per deploy and served to the browser at runtime (utils/public-env.ts).
 *   Available on both sides, which is why it is the fallback rather than the
 *   primary.
 *
 * Dependency-free on purpose: `src/proxy.ts` imports the cookie helpers built
 * on this, and the proxy runtime must not pull in `server-only` or db modules.
 */

/** Values `APP_ENV` takes on a real deployment. Anything else is local. */
const DEPLOYED_APP_ENVS = new Set(['prod', 'stage', 'next'])

/** Does this `APP_ENV` value name a real deployment (prod / stage / next)? */
function isDeployedAppEnv(appEnv: string | undefined): boolean {
  return DEPLOYED_APP_ENVS.has((appEnv ?? '').trim().toLowerCase())
}

/** Hostname of the configured auth URL, or '' when unset/unparseable. */
function authUrlHost(): string {
  const raw = publicEnv('NEXT_PUBLIC_AUTH_URL') ?? ''
  if (!raw) return ''
  try {
    return new URL(raw).hostname.toLowerCase()
  } catch {
    return ''
  }
}

/** Is the app served from a real WorldVue host (so a shared cookie domain applies)? */
export function isWorldVueAuthHost(): boolean {
  const host = authUrlHost()
  return host === 'worldvue.com' || host.endsWith('.worldvue.com')
}

/**
 * Can a `Secure` cookie actually survive here?
 *
 * The literal truth — only https carries one. A local production build is http,
 * so it must NOT set `Secure` / `sameSite: 'None'` even though `NODE_ENV` says
 * production. Every place that names or sets an auth cookie has to agree on
 * this: the writer (better-auth's `defaultCookieAttributes`) and the readers
 * (the `__Secure-` prefix in `getAuthCookiePrefix`). They were three separate
 * copies of the same expression, so fixing one alone moved the breakage instead
 * of removing it — the app would set `dev_.session_token` and then look for
 * `__Secure-dev_.session_token`.
 */
export function isSecureAuthUrl(): boolean {
  return (publicEnv('NEXT_PUBLIC_AUTH_URL') ?? '').startsWith('https://')
}

/**
 * Is this the stage deployment specifically?
 *
 * Only for the handful of places that address stage infrastructure by name (the
 * files container). Prefer `isDeployedEnvironment()` for anything that is really
 * asking "deployed or local".
 */
export function isStageDeployment(): boolean {
  const appEnv = (process.env.APP_ENV ?? '').trim().toLowerCase()
  if (appEnv) return appEnv === 'stage'
  // No APP_ENV: fall back to the host, matched on the hostname rather than as a
  // substring of the whole URL (a path or query containing "stage" is not stage).
  return authUrlHost() === 'atlas.stage.worldvue.com'
}

/**
 * Is this a deployed environment (prod / stage / next) rather than a local run?
 *
 * Governs the things that should follow the *deployment*, not the protocol:
 * trusted origins, CSRF enforcement, and whether the email allowlist falls back
 * to its safe list.
 *
 * **Fails safe by design.** A deployment that somehow has neither `APP_ENV` nor
 * a WorldVue auth URL reads as local, which means the email guard restricts
 * rather than sends. That trade is deliberate: not delivering mail is a visible,
 * recoverable outage, and emailing real users from a machine that thinks it is
 * prod is neither. `APP_ENV` is reliably set on deploys — the session cookie
 * prefix and the DocuSeal context both already depend on it, so a deploy
 * missing it is broken long before it reaches here.
 */
export function isDeployedEnvironment(): boolean {
  if (isDeployedAppEnv(process.env.APP_ENV)) return true
  // No APP_ENV (browser, or a var that never made it into the container):
  // fall back to the address the app is served from.
  return isSecureAuthUrl() && isWorldVueAuthHost()
}

/**
 * Is this production itself, not stage or next?
 *
 * For guards on side effects that land in a shared production system: stage,
 * next and local all talk to the same Dynamics tenant as prod, so "deployed"
 * isn't enough. Fails safe like `isDeployedEnvironment`: no `APP_ENV` and no
 * prod host reads as not production.
 */
export function isProductionDeployment(): boolean {
  const appEnv = (process.env.APP_ENV ?? '').trim().toLowerCase()
  if (appEnv) return appEnv === 'prod'
  return isSecureAuthUrl() && authUrlHost() === 'atlas.worldvue.com'
}

/**
 * The `environment` tag for Sentry.
 *
 * Stage and prod run the same image, so this has to come from the deployment
 * rather than the build. Prod keeps reporting as `production` (what `NODE_ENV`
 * always gave it) so existing alert rules keep matching; stage gets its own tag.
 *
 * The browser falls back to the page's own hostname, because the client
 * `Sentry.init` runs before `/api/public-env` has loaded (Next requires
 * instrumentation-client ahead of `beforeInteractive` scripts) and has to be
 * right at init: sessions, replays and trace headers never pass beforeSend.
 */
export function sentryEnvironment(): string {
  const appEnv = (publicEnv('APP_ENV') ?? '').trim().toLowerCase()
  if (appEnv === 'prod') return 'production'
  if (appEnv) return appEnv
  if (typeof window !== 'undefined') {
    const host = window.location.hostname.toLowerCase()
    if (host === 'atlas.worldvue.com') return 'production'
    if (host === 'atlas.stage.worldvue.com') return 'stage'
  }
  return process.env.NODE_ENV || 'development'
}
